import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Mail, Lock, User as UserIcon, AlertTriangle, ArrowLeft, ShieldCheck, X } from 'lucide-react';
import {
  login, signup, requestOtp, verifyOtp,
  loginWithGoogle, loginWithFacebook, fbLoginPopup,
  GOOGLE_CLIENT_ID, FACEBOOK_APP_ID,
} from '../lib/auth';
import { useAuth } from '../contexts/AuthContext';
import './LoginModal.css';

// New auth flow (this is what the user asked for):
//   • Sign in  = email + password (unchanged)
//   • Create account = enter name + email → click "Create account"
//                      → backend mails a 6-digit code (verifyOtp/upsertUser
//                      creates the user record) → user enters the code →
//                      account ready, signed in.
// OTP is no longer a separate top-level tab — it IS the signup verification.
// OAuth (Google/Facebook) stays at the bottom.
export default function LoginModal({ onClose, setUser: setUserProp } = {}) {
  // Accept setUser as a prop (from App's on-demand controller) OR fall
  // back to the AuthContext so the component still works in isolation.
  const ctx = useAuth();
  const setUser = setUserProp || ctx.setUser;

  const [mode, setMode] = useState('signin');   // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');         // Username (required on signup)
  const [dob, setDob] = useState('');           // YYYY-MM-DD from <input type="date">
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // OTP state — only used in the signup flow (mode === 'signup').
  const [otpStage, setOtpStage] = useState('details'); // 'details' | 'verify'
  const [otpCode, setOtpCode] = useState('');

  // In-modal legal viewer — opens Privacy / Terms in an overlay above
  // the login card instead of a new tab.
  const [legalView, setLegalView] = useState(null); // null | 'privacy' | 'terms'

  // Min age 18 — signups below 18 are not allowed. Compute the latest
  // DOB that still makes the user 18+ for use as the `max` attribute.
  const MIN_AGE = 18;
  const today = new Date();
  const minAdultDate = new Date(today.getFullYear() - MIN_AGE, today.getMonth(), today.getDate());
  const dobMax = minAdultDate.toISOString().slice(0, 10);

  const isAdult = (() => {
    if (!dob) return false;
    const d = new Date(dob);
    if (isNaN(d.getTime())) return false;
    const ageYears = (today - d) / (1000 * 60 * 60 * 24 * 365.25);
    return ageYears >= MIN_AGE;
  })();
  const signupReady = !!email && !!name.trim() && isAdult && agreedTerms && !busy;

  // Inline GIS button host — mounted once on modal open.
  const googleBtnRef = useRef(null);
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;
    const tryMount = () => {
      if (cancelled) return;
      if (!window.google?.accounts?.id) { setTimeout(tryMount, 200); return; }
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (resp) => {
          if (!resp?.credential) return;
          setBusy(true); setError('');
          try {
            const u = await loginWithGoogle({ idToken: resp.credential });
            setUser(u);
          } catch (err) {
            setError(err.message || 'Google sign-in failed.');
          } finally { setBusy(false); }
        },
      });
      if (googleBtnRef.current) {
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: document.body.classList.contains('dark') ? 'filled_black' : 'outline',
          size: 'medium',
          shape: 'pill',
          text: 'continue_with',
        });
      }
    };
    tryMount();
    return () => { cancelled = true; };
  }, [setUser]);

  // Esc closes the modal — but only when on-demand (the App.js owner
  // provided onClose). If used standalone, Esc does nothing.
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const switchMode = (m) => {
    setMode(m);
    setError('');
    setOtpStage('details');
    setOtpCode('');
    setPassword('');
  };

  // ── Sign in: email + password
  const submitSignIn = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const u = await login({ email, password });
      setUser(u);
    } catch (err) {
      setError(err.message || 'Sign-in failed.');
    } finally { setBusy(false); }
  };

  // ── Signup step 1: enter name+email, click → send OTP
  const submitSignupDetails = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await requestOtp({ email });
      setOtpStage('verify');
    } catch (err) {
      setError(err.message || "Couldn't send the code. Try again.");
    } finally { setBusy(false); }
  };

  // ── Signup step 2: enter OTP, click → verifyOtp creates account + session
  const submitSignupOtp = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const u = await verifyOtp({ email, code: otpCode, name });
      setUser(u);
    } catch (err) {
      setError(err.message || 'Verification failed.');
    } finally { setBusy(false); }
  };

  const doFacebook = async () => {
    if (!FACEBOOK_APP_ID) {
      setError('Facebook login is not configured for this environment.');
      return;
    }
    setBusy(true); setError('');
    try {
      const accessToken = await fbLoginPopup();
      const u = await loginWithFacebook({ accessToken });
      setUser(u);
    } catch (err) {
      setError(err.message || 'Facebook sign-in failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="login-overlay" role="dialog" aria-modal="true" onClick={(e) => {
      // Click on the dark backdrop closes the modal (when on-demand).
      if (onClose && e.target === e.currentTarget) onClose();
    }}>
      <div className="login-card">
        {onClose && (
          <button className="login-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        )}

        <div className="login-brand">
          <img
            src={`${process.env.PUBLIC_URL || ''}/arthum-pandit-sm.png`}
            alt=""
            style={{ width: 44, height: 'auto', flexShrink: 0 }}
            aria-hidden
          />
          <div>
            <h1>Welcome to Sutrum AI</h1>
            <p>Sign in to chat, generate images, and save your history.</p>
          </div>
        </div>

        {/* Sign in / Create account toggle replaces the old multi-tab strip. */}
        <div className="login-toggle">
          {['signin', 'signup'].map((m) => (
            <button
              key={m}
              type="button"
              className={mode === m ? 'on' : ''}
              onClick={() => switchMode(m)}
            >
              {m === 'signin' ? 'Sign in' : 'Create account'}
            </button>
          ))}
        </div>

        <div className="login-body">
          {error && (
            <div className="login-error">
              <AlertTriangle size={14} /> <span>{error}</span>
            </div>
          )}

          {/* ── Sign in: classic email + password ── */}
          {mode === 'signin' && (
            <form onSubmit={submitSignIn} className="login-form">
              <Field icon={<Mail size={14} />} label="Email">
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" required autoComplete="email"
                />
              </Field>
              <Field icon={<Lock size={14} />} label="Password">
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password" required minLength={6}
                  autoComplete="current-password"
                />
              </Field>
              <button type="submit" className="login-submit" disabled={busy}>
                {busy && <Loader2 size={14} className="spin" />}
                Sign in
              </button>
              <p className="login-hint" style={{ textAlign: 'center', marginTop: 2 }}>
                Don't have an account?{' '}
                <button type="button" className="login-linklike" onClick={() => switchMode('signup')}>
                  Create one
                </button>
              </p>
            </form>
          )}

          {/* ── Create account, step 1: username + email + DOB + T&C ── */}
          {mode === 'signup' && otpStage === 'details' && (
            <form onSubmit={submitSignupDetails} className="login-form">
              <p className="login-hint">
                We'll email a 6-digit code to verify your address. No password needed.
              </p>
              <Field icon={<UserIcon size={14} />} label="Username">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Pick a username"
                  required
                  minLength={2}
                  maxLength={40}
                  autoComplete="username"
                />
              </Field>
              <Field icon={<Mail size={14} />} label="Email">
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" required autoComplete="email"
                />
              </Field>
              <Field icon={<UserIcon size={14} />} label="Date of birth">
                <input
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  max={dobMax}
                  required
                  placeholder="YYYY-MM-DD"
                  className={dob ? '' : 'date-empty'}
                />
                {dob && !isAdult && (
                  <span className="login-fieldhint login-fieldhint-warn">
                    You must be at least 18 to create an account.
                  </span>
                )}
              </Field>

              {/* T&C consent — unchecked by default. Both links open the
                  legal documents inside this modal (not a new tab) so the
                  user never loses their place. Submit stays disabled until
                  the box is explicitly checked. */}
              <label className="login-consent">
                <input
                  type="checkbox"
                  checked={agreedTerms}
                  onChange={(e) => setAgreedTerms(e.target.checked)}
                />
                <span>
                  I am 18 or older and agree to the{' '}
                  <button
                    type="button"
                    className="login-linklike"
                    onClick={(e) => { e.preventDefault(); setLegalView('terms'); }}
                  >Terms of Service</button>
                  {' '}and consent to the processing of my data as described in the{' '}
                  <button
                    type="button"
                    className="login-linklike"
                    onClick={(e) => { e.preventDefault(); setLegalView('privacy'); }}
                  >Privacy Policy</button>.
                </span>
              </label>

              <button type="submit" className="login-submit" disabled={!signupReady}>
                {busy && <Loader2 size={14} className="spin" />}
                Create account
              </button>
              <p className="login-hint" style={{ textAlign: 'center', marginTop: 2 }}>
                Already have an account?{' '}
                <button type="button" className="login-linklike" onClick={() => switchMode('signin')}>
                  Sign in
                </button>
              </p>
            </form>
          )}

          {/* ── Create account, step 2: enter the OTP we just emailed ── */}
          {mode === 'signup' && otpStage === 'verify' && (
            <form onSubmit={submitSignupOtp} className="login-form">
              <button type="button" className="login-back" onClick={() => setOtpStage('details')}>
                <ArrowLeft size={12} /> Change email
              </button>
              <p className="login-hint">
                A 6-digit code was sent to <strong>{email}</strong>. Check your inbox (and Spam folder).
              </p>
              <Field icon={<ShieldCheck size={14} />} label="6-digit code">
                <input
                  inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000" required autoFocus
                />
              </Field>
              <button type="submit" className="login-submit" disabled={busy || otpCode.length < 6}>
                {busy && <Loader2 size={14} className="spin" />}
                Verify & create account
              </button>
              <button type="button" className="login-resend" onClick={submitSignupDetails} disabled={busy}>
                Resend code
              </button>
            </form>
          )}
        </div>

        {/* ── OAuth section at the bottom ── */}
        {(GOOGLE_CLIENT_ID || FACEBOOK_APP_ID) && (
          <div className="login-oauth-section">
            <div className="login-divider">or continue with</div>
            <div className="login-oauth-row">
              {GOOGLE_CLIENT_ID ? (
                <div ref={googleBtnRef} className="google-btn-host" />
              ) : (
                <button type="button" className="oauth-btn" disabled title="Google sign-in not configured">
                  <GoogleIcon /> Google
                </button>
              )}
              {FACEBOOK_APP_ID ? (
                <button type="button" className="oauth-btn" onClick={doFacebook} disabled={busy}>
                  {busy ? <Loader2 size={14} className="spin" /> : <FbIcon />}
                  Facebook
                </button>
              ) : (
                <button type="button" className="oauth-btn" disabled title="Facebook sign-in not configured">
                  <FbIcon /> Facebook
                </button>
              )}
            </div>
          </div>
        )}

        <p className="login-foot">
          By continuing you agree to our{' '}
          <button type="button" className="login-linklike" onClick={() => setLegalView('terms')}>Terms</button>
          {' '}and{' '}
          <button type="button" className="login-linklike" onClick={() => setLegalView('privacy')}>Privacy Policy</button>.
        </p>
      </div>

      {legalView && (
        <LegalViewer
          which={legalView}
          onClose={() => setLegalView(null)}
        />
      )}
    </div>
  );
}

// In-modal viewer for Terms / Privacy. Renders the existing static
// /privacy.html (with #terms anchor) inside an iframe overlay on top
// of the login card so the user can read it without losing their
// half-filled signup form.
function LegalViewer({ which, onClose }) {
  const src = which === 'terms' ? '/privacy.html#terms' : '/privacy.html';
  const title = which === 'terms' ? 'Terms of Service' : 'Privacy Policy';
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="legal-overlay" role="dialog" aria-modal="true" aria-label={title}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="legal-card">
        <header className="legal-header">
          <h2>{title}</h2>
          <button type="button" className="legal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>
        <iframe
          src={src}
          title={title}
          className="legal-frame"
          sandbox="allow-same-origin allow-popups"
        />
      </div>
    </div>
  );
}

function Field({ icon, label, children }) {
  return (
    <label className="login-field">
      <span>{icon} {label}</span>
      {children}
    </label>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function FbIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#1877f2" aria-hidden>
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
    </svg>
  );
}
